import React from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RootState } from '@/redux/store';
import { useToast } from '@/components/ui/use-toast';
import { useGetDashboardConfigQuery, useUpdateDashboardConfigMutation } from '@/services/dashboardConfigApi';
import {
  FinancialConfigFormData,
  financialConfigSchema,
  defaultFinancialConfigValues,
} from '@/types/dashboard-config';
import ConfigCard from './components/ConfigCard';
import NumberInput from './components/NumberInput';
import { LoadingButton } from '@/components/ui/common/loading-button';
import { ErrorType } from '@/types';

interface FinancialSectionProps {
  refetch: () => void;
}

export default function FinancialSection({ refetch }: FinancialSectionProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const [updateConfig, { isLoading }] = useUpdateDashboardConfigMutation();
  const { data: configData } = useGetDashboardConfigQuery({ orgId });

  const form = useForm<FinancialConfigFormData>({
    mode: 'all',
    resolver: zodResolver(financialConfigSchema),
    defaultValues: defaultFinancialConfigValues,
  });

  const { handleSubmit, formState: { errors }, reset, setValue, watch } = form;
  const [isEditing, setIsEditing] = React.useState(false);

  const resetToLoaded = React.useCallback(() => {
    if (configData?.dashboard_configuration) {
      const config = configData.dashboard_configuration;
      reset({
        financial_memberships_revenue_target_daily: config.financial_memberships_revenue_target_daily || '',
        financial_memberships_revenue_target_weekly: config.financial_memberships_revenue_target_weekly || '',
        financial_memberships_revenue_target_monthly: config.financial_memberships_revenue_target_monthly || '',
        financial_memberships_revenue_target_6months: config.financial_memberships_revenue_target_6months || '',
        financial_memberships_revenue_target_yearly: config.financial_memberships_revenue_target_yearly || '',
        financial_facility_bookings_revenue_target_daily: config.financial_facility_bookings_revenue_target_daily || '',
        financial_facility_bookings_revenue_target_weekly: config.financial_facility_bookings_revenue_target_weekly || '',
        financial_facility_bookings_revenue_target_monthly: config.financial_facility_bookings_revenue_target_monthly || '',
        financial_facility_bookings_revenue_target_6months: config.financial_facility_bookings_revenue_target_6months || '',
        financial_facility_bookings_revenue_target_yearly: config.financial_facility_bookings_revenue_target_yearly || '',
        financial_products_revenue_target_daily: config.financial_products_revenue_target_daily || '',
        financial_products_revenue_target_weekly: config.financial_products_revenue_target_weekly || '',
        financial_products_revenue_target_monthly: config.financial_products_revenue_target_monthly || '',
        financial_products_revenue_target_6months: config.financial_products_revenue_target_6months || '',
        financial_products_revenue_target_yearly: config.financial_products_revenue_target_yearly || '',
        financial_credits_revenue_target_daily: config.financial_credits_revenue_target_daily || '',
        financial_credits_revenue_target_weekly: config.financial_credits_revenue_target_weekly || '',
        financial_credits_revenue_target_monthly: config.financial_credits_revenue_target_monthly || '',
        financial_credits_revenue_target_6months: config.financial_credits_revenue_target_6months || '',
        financial_credits_revenue_target_yearly: config.financial_credits_revenue_target_yearly || '',
        financial_training_revenue_target_daily: config.financial_training_revenue_target_daily || '',
        financial_training_revenue_target_weekly: config.financial_training_revenue_target_weekly || '',
        financial_training_revenue_target_monthly: config.financial_training_revenue_target_monthly || '',
        financial_training_revenue_target_6months: config.financial_training_revenue_target_6months || '',
        financial_training_revenue_target_yearly: config.financial_training_revenue_target_yearly || '',
        financial_large_refund_threshold: config.financial_large_refund_threshold || '',
      });
    }
  }, [configData, reset]);

  React.useEffect(() => {
    resetToLoaded();
  }, [resetToLoaded]);

  const handleCancelEdit = React.useCallback(() => {
    setIsEditing(false);
    resetToLoaded();
  }, [resetToLoaded]);

  const onSubmit = async (formData: FinancialConfigFormData) => {
    try {
      const config: Record<string, string> = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          config[key] = String(value);
        }
      });

      await updateConfig({ dashboard_configuration: config }).unwrap();
      toast({ variant: 'success', title: 'Financial targets saved successfully' });
      setIsEditing(false);
      refetch();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: 'destructive',
        title: 'Error',
        description: typedError.data?.detail || typedError.data?.message || 'Failed to update financial targets',
      });
    }
  };

  const onError = (fieldErrors: any) => {
    console.error('Financial form validation errors:', fieldErrors);
    toast({ variant: 'destructive', description: 'Please check the entered values and fix any validation errors' });
  };

  const revenueRows = [
    { prefix: 'financial_memberships_revenue_target', label: 'Memberships' },
    { prefix: 'financial_facility_bookings_revenue_target', label: 'Facility bookings' },
    { prefix: 'financial_products_revenue_target', label: 'Products' },
    { prefix: 'financial_credits_revenue_target', label: 'Credits' },
    { prefix: 'financial_training_revenue_target', label: 'Training' },
  ];

  const columns = [
    { key: 'daily', label: 'Daily' },
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: '6months', label: 'Last 6 months' },
    { key: 'yearly', label: 'Yearly' },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col gap-4">
      {/* Revenue Targets Table Section */}
      <ConfigCard
        title="Revenue targets"
        description="Drives the target vs actual chart. Values in PKR."
      >
        <div className="-mx-1 overflow-x-auto px-1">
          <table className="w-full min-w-[680px] border-separate border-spacing-0">
            <thead>
              <tr>
                <th className="w-40 pb-2 text-left text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
                  Revenue stream
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="pb-2 pl-3 text-right text-[12px] font-medium uppercase tracking-wide text-muted-foreground"
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {revenueRows.map((row) => (
                <tr key={row.prefix}>
                  <td className="border-t border-border py-2.5 pr-3 align-top text-[13px] text-foreground">
                    <span className="inline-block pt-2">{row.label}</span>
                  </td>
                  {columns.map((col) => {
                    const fieldKey = `${row.prefix}_${col.key}` as keyof FinancialConfigFormData;
                    return (
                      <td key={col.key} className="border-t border-border py-2.5 pl-3 align-top">
                        {isEditing ? (
                          <NumberInput
                            aria-label={`${row.label} ${col.label}`}
                            value={String(watch(fieldKey as any) || '')}
                            onChange={(value) => setValue(fieldKey as any, value, { shouldValidate: true, shouldDirty: true })}
                            placeholder={`${col.label} target`}
                            min={1}
                            error={(errors as any)[fieldKey]?.message}
                          />
                        ) : (
                          <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
                            {String(watch(fieldKey as any) || 'Not set')}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ConfigCard>

      {/* Large Refund Threshold Section */}
      <ConfigCard
        title="Large refund threshold"
        description="Refunds at or above this amount count toward 'Large refunds this month'."
      >
        <div className="max-w-xs">
          {isEditing ? (
            <NumberInput
              aria-label="Large refund threshold"
              value={String(watch('financial_large_refund_threshold') || '')}
              onChange={(value) => setValue('financial_large_refund_threshold', value, { shouldValidate: true, shouldDirty: true })}
              prefix="PKR"
              placeholder="Large refund threshold"
              min={1}
              error={errors.financial_large_refund_threshold?.message}
            />
          ) : (
            <div className="flex min-h-[38px] items-center justify-end rounded-lg border border-border bg-muted/40 px-2.5 py-2 text-[13px] text-foreground">
              {String(watch('financial_large_refund_threshold') || 'Not set')}
            </div>
          )}
        </div>
      </ConfigCard>

      {/* Bottom Action Buttons */}
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        {!isEditing ? (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            disabled={isLoading}
            className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
          >
            Edit
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isLoading}
              className="rounded-lg border border-border bg-card px-3.5 py-2 text-[13px] font-medium text-foreground transition-colors hover:bg-muted/50 disabled:opacity-50"
            >
              Cancel
            </button>

            <LoadingButton
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="rounded-lg px-3.5 py-2 text-[13px] font-medium transition-colors"
            >
              Save changes
            </LoadingButton>
          </>
        )}
      </div>
    </form>
  );
}


